// src/pages/AdminUsers.js - Working version
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  UsersIcon,
  PlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

export default function AdminUsers() {
  const { isSuperAdmin } = useAuth()
  const [users, setUsers] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newUser, setNewUser] = useState({
    email: '',
    businessPhone: '',
    role: 'viewer'
  })

  const SUPER_ADMINS = [''] // Keep in sync with AuthContext

  useEffect(() => {
    if (isSuperAdmin()) {
      loadUsers()
      loadBusinesses()
    }
  }, [])

  const loadUsers = async () => {
    try {
      console.log('🔄 Loading users...')
      
      // Get users from Supabase auth
      const { data: authData, error: authError } = await supabase.auth.admin.listUsers()
      
      if (authError) {
        console.error('❌ Auth error:', authError)
        throw authError
      }
      
      console.log('👥 Auth users loaded:', authData.users.length)

      // Get business assignments
      let businessAssignments = []
      try {
        const { data: assignments, error: assignError } = await supabase
          .from('user_businesses')
          .select('user_id, business_phone, role')

        if (assignError) {
          console.warn('⚠️ Business assignments error:', assignError.message)
          // Continue without assignments
        } else {
          businessAssignments = assignments || []
          console.log('🏢 Business assignments loaded:', businessAssignments.length)
        }
      } catch (err) {
        console.warn('⚠️ user_businesses table might not exist yet:', err.message)
      }

      // Get business info
      let businessInfo = []
      try {
        const { data: businesses, error: businessError } = await supabase
          .from('business_info')
          .select('phone_number, business_name')

        if (businessError) {
          console.warn('⚠️ Business info error:', businessError.message)
        } else {
          businessInfo = businesses || []
          console.log('🏪 Business info loaded:', businessInfo.length)
        }
      } catch (err) {
        console.warn('⚠️ Could not load business info:', err.message)
      }

      // Combine data
      const combinedUsers = authData.users.map(authUser => {
        const assignments = businessAssignments.filter(a => a.user_id === authUser.id)
        const isSuperAdmin = SUPER_ADMINS.includes(authUser.email)
        
        return {
          id: authUser.id,
          email: authUser.email,
          created_at: authUser.created_at,
          last_sign_in_at: authUser.last_sign_in_at,
          role: isSuperAdmin ? 'super_admin' : 'business_user',
          businesses: assignments.map(a => {
            const businessData = businessInfo.find(b => b.phone_number === a.business_phone)
            return {
              phone: a.business_phone,
              name: businessData?.business_name || a.business_phone,
              role: a.role
            }
          })
        }
      })

      console.log('✅ Combined users:', combinedUsers.length)
      setUsers(combinedUsers)
    } catch (error) {
      console.error('❌ Error loading users:', error)
      alert('Error loading users: ' + error.message)
    }
  }

  const loadBusinesses = async () => {
    try {
      const { data, error } = await supabase
        .from('business_info')
        .select('phone_number, business_name, business_type')
        .eq('active', true)
        .order('business_name')

      if (error) {
        console.error('❌ Error loading businesses:', error)
        throw error
      }
      
      console.log('📋 Businesses for dropdown:', data?.length || 0)
      setBusinesses(data || [])
    } catch (error) {
      console.error('Error loading businesses:', error)
      // Don't show alert for this, just log it
    } finally {
      setLoading(false)
    }
  }

  const createUserAndAssign = async () => {
    if (!newUser.email || !newUser.businessPhone) {
      alert('Please fill in all required fields')
      return
    }

    try {
      console.log('🆕 Creating user:', newUser.email)
      
      // Generate a temporary password
      const tempPassword = Math.random().toString(36).slice(-12) + 'A1!'

      // 1. Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: newUser.email,
        password: tempPassword,
        email_confirm: true
      })

      if (authError) {
        console.error('❌ Auth creation error:', authError)
        throw authError
      }

      console.log('✅ User created in auth:', authData.user.id)

      // 2. Assign to business
      const { error: assignError } = await supabase
        .from('user_businesses')
        .insert({
          user_id: authData.user.id,
          business_phone: newUser.businessPhone,
          role: newUser.role
        })

      if (assignError) {
        console.error('❌ Business assignment error:', assignError)
        throw assignError
      }

      console.log('✅ User assigned to business')

      // 3. Send password reset email
      try {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(newUser.email, {
          redirectTo: `${window.location.origin}/reset-password`
        })

        if (resetError) {
          console.warn('⚠️ Password reset email error:', resetError)
        } else {
          console.log('✅ Password reset email sent')
        }
      } catch (resetErr) {
        console.warn('⚠️ Could not send password reset email:', resetErr)
      }

      alert(`User created successfully! They will receive an email to set their password.`)
      setShowCreateModal(false)
      setNewUser({ email: '', businessPhone: '', role: 'viewer' })
      loadUsers()
    } catch (error) {
      console.error('❌ Error creating user:', error)
      alert('Error creating user: ' + error.message)
    }
  }

  const removeUserFromBusiness = async (userId, businessPhone) => {
    if (!window.confirm('Remove this user from the business?')) return

    try {
      const { error } = await supabase
        .from('user_businesses')
        .delete()
        .eq('user_id', userId)
        .eq('business_phone', businessPhone)

      if (error) throw error
      
      console.log('✅ User removed from business')
      loadUsers()
    } catch (error) {
      console.error('❌ Error removing user:', error)
      alert('Error removing user: ' + error.message)
    }
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return ''
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1)
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`
    }
    return phone
  }

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      business_user: 'bg-blue-100 text-blue-800'
    }
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[role] || colors.business_user}`}>
        {role === 'super_admin' && <ShieldCheckIcon className="w-3 h-3 mr-1" />}
        {role === 'super_admin' ? 'Super Admin' : 'Business User'}
      </span>
    )
  }

  if (!isSuperAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <UsersIcon className="w-6 h-6 text-gray-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Create User
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="p-6">
          {/* Users Table */}
          <div className="bg-white shadow-sm rounded-lg border">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          <div className="text-sm text-gray-500">
                            Created {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {user.businesses && user.businesses.length > 0 ? 
                            user.businesses.map((business, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{business.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {formatPhoneNumber(business.phone)} • {business.role}
                                  </div>
                                </div>
                                {user.role !== 'super_admin' && (
                                  <button
                                    onClick={() => removeUserFromBusiness(user.id, business.phone)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            )) : 
                            user.role === 'super_admin' ? 
                              <span className="text-sm text-gray-500">All businesses</span> :
                              <span className="text-sm text-gray-500">No business access</span>
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {user.role !== 'super_admin' && user.businesses.length > 0 && (
                          <span className="text-sm text-gray-500">Manage</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {users.length === 0 && (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  There might be an issue loading users from Supabase Auth.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Business User</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="user@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Business</label>
                  <select
                    value={newUser.businessPhone}
                    onChange={(e) => setNewUser({ ...newUser, businessPhone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Select a business...</option>
                    {businesses.map((business) => (
                      <option key={business.phone_number} value={business.phone_number}>
                        {business.business_name} - {formatPhoneNumber(business.phone_number)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createUserAndAssign}
                  disabled={!newUser.email || !newUser.businessPhone}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}